import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { type FC, useState } from 'react';

import { timezoneMap } from '../../../extViewmodels/Utils';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../web/components-v2/Select';
import { FollowUpOptionIds } from '../../viewModels/phonecall';
import { CustomFollowUpModal } from './CustomFollowUpModal';
import type { FollowUp } from './types';

/** One follow-up option, either server-driven (per-outcome) or static fallback. */
export type FollowUpSelectOption = {
	id: string;
	label: string;
};

export type FollowUpSelectProps = {
	value: FollowUp | null;
	onChange: (next: FollowUp | null) => void;
	/** Visible label, defaults to "Follow Up:". */
	label?: string;
	/**
	 * Optional override of the option list. When provided (and non-empty), the
	 * dropdown renders these options instead of the static `ORDERED_OPTIONS`
	 * fallback. Surfaced by `useFollowUpOptions` as the per-outcome list once
	 * the rep has picked an outcome — the server response gates which
	 * shortcuts are valid for that outcome.
	 */
	options?: ReadonlyArray<FollowUpSelectOption>;
	/** The lead's IANA zone. Follow-ups are scheduled against the prospect's clock, not the rep's. */
	leadTimeZone?: string;
};

const NONE_VALUE = '__none__';
const CUSTOM_VALUE = '__custom__';

const FOLLOW_UP_LABELS: Record<FollowUpOptionIds, string> = {
	[FollowUpOptionIds.FiteenMinutes]: 'In 15 minutes',
	[FollowUpOptionIds.ThirtyMinutes]: 'In 30 minutes',
	[FollowUpOptionIds.OneHour]: 'In 1 hour',
	[FollowUpOptionIds.TwoHours]: 'In 2 hours',
	[FollowUpOptionIds.ThreeHours]: 'In 3 hours',
	[FollowUpOptionIds.ThisAfternoon]: 'This afternoon',
	[FollowUpOptionIds.TomrrowMorning]: 'Tomorrow morning',
	[FollowUpOptionIds.Tomorrow]: 'Tomorrow',
	[FollowUpOptionIds.Monday]: 'Monday',
	[FollowUpOptionIds.Tuesday]: 'Tuesday',
	[FollowUpOptionIds.Wednesday]: 'Wednesday',
	[FollowUpOptionIds.ThreeDays]: 'In 3 days',
	[FollowUpOptionIds.OneWeek]: 'In 1 week',
	[FollowUpOptionIds.TwoWeeks]: 'In 2 weeks',
};

const ORDERED_OPTIONS: ReadonlyArray<FollowUpOptionIds> = [
	FollowUpOptionIds.FiteenMinutes,
	FollowUpOptionIds.ThirtyMinutes,
	FollowUpOptionIds.OneHour,
	FollowUpOptionIds.TwoHours,
	FollowUpOptionIds.ThreeHours,
	FollowUpOptionIds.ThisAfternoon,
	FollowUpOptionIds.TomrrowMorning,
	FollowUpOptionIds.Tomorrow,
	FollowUpOptionIds.Monday,
	FollowUpOptionIds.Tuesday,
	FollowUpOptionIds.Wednesday,
	FollowUpOptionIds.ThreeDays,
	FollowUpOptionIds.OneWeek,
	FollowUpOptionIds.TwoWeeks,
];

/**
 * Formats a scheduled follow-up for display in the Select trigger:
 * `"<name> • M/D/YY h:mm a TZ"`. Mirrors the legacy
 * `aida/components/followup/FollowUpSelect:128-134` format so the focused-view
 * dropdown reads the same as the queue dropdown. The TZ abbreviation comes
 * from `userPreferences.timeZone` via `timezoneMap`; when that lookup misses
 * (e.g. an unmapped IANA zone) the suffix is omitted rather than left as a
 * raw "America/Foo" string.
 */
const formatScheduled = (
	fu: { date: string; name: string },
	tzAbbrev: string | null,
	zone: string | undefined
): string => {
	const m = zone ? momentTz(fu.date).tz(zone) : moment(fu.date);
	if (!m.isValid()) return fu.name;
	const stamp = m.format('M/D/YY h:mm a');
	return tzAbbrev ? `${fu.name} • ${stamp} ${tzAbbrev}` : `${fu.name} • ${stamp}`;
};

/**
 * Follow-up picker for the wrap-up workflow.
 *
 * Surfaces the predefined `FollowUpOptionIds` set + "None" + a "At a specific
 * time…" custom entry. The custom entry opens the `CustomFollowUpModal`, and
 * on confirm fires `onChange` with a `FollowUp.scheduled` payload.
 *
 * Built on the components-v2 Select primitive, which exposes a controlled
 * `value` + `onValueChange` string-in/string-out contract. We map "None" to a
 * sentinel value (`__none__`) and "Custom" to (`__custom__`) since the Base UI
 * Select can't carry `null` or open-modal semantics directly; this component
 * translates those sentinels back to the parent's `FollowUp | null` shape.
 */
export const FollowUpSelect: FC<FollowUpSelectProps> = ({
	value,
	onChange,
	label = 'Follow Up:',
	options,
	leadTimeZone,
}) => {
	const [isModalOpen, setModalOpen] = useState(false);
	const userSession = useUserSession();
	// Resolve the user's TZ abbreviation (CST/EST/PST/...) for the trigger
	// label. Falls back to the browser's guessed zone, then null if neither
	// resolves to one of the abbreviations in `timezoneMap`.
	const zone = leadTimeZone && momentTz.tz.zone(leadTimeZone) ? leadTimeZone : undefined;
	const tzKey = zone || userSession?.user?.userPreferences?.timeZone || momentTz.tz.guess();
	const tzAbbrev = (tzKey && timezoneMap[tzKey]) || (zone ? momentTz.tz(zone).zoneAbbr() : null);

	// Use server-provided options when available (per-outcome list from
	// `useFollowUpOptions`); otherwise fall back to the static curated list so
	// the dropdown still renders before any outcome is picked.
	const renderedOptions: ReadonlyArray<FollowUpSelectOption> =
		options && options.length > 0 ? options : ORDERED_OPTIONS.map(id => ({ id, label: FOLLOW_UP_LABELS[id] ?? id }));

	const labelById = (id: string): string => {
		const fromOptions = options?.find(o => o.id === id);
		if (fromOptions) return fromOptions.label;
		return FOLLOW_UP_LABELS[id as FollowUpOptionIds] ?? id;
	};

	let currentValue: string;
	let triggerLabel: string;
	if (value?.kind === 'option') {
		currentValue = value.optionId;
		triggerLabel = labelById(value.optionId);
	} else if (value?.kind === 'scheduled') {
		// Scheduled values can't be represented as a real Select option (the
		// payload is unbounded); we still need a stable `value` for the Base UI
		// Select to render. Reuse `CUSTOM_VALUE` so the Select stays a
		// controlled component and clicking "At a specific time…" again still
		// opens the modal pre-filled.
		currentValue = CUSTOM_VALUE;
		triggerLabel = formatScheduled(value, tzAbbrev, zone);
	} else {
		currentValue = NONE_VALUE;
		triggerLabel = 'None';
	}

	const handleChange = (next: string) => {
		if (next === NONE_VALUE) {
			onChange(null);
			return;
		}
		if (next === CUSTOM_VALUE) {
			setModalOpen(true);
			return;
		}
		onChange({ kind: 'option', optionId: next });
	};

	const initialForModal =
		value?.kind === 'scheduled' ? { name: value.name, date: value.date, importance: value.importance } : undefined;

	return (
		<div className={css(styles.root)}>
			<span className={css(styles.label)}>{label}</span>
			<Select value={currentValue} onValueChange={handleChange}>
				<SelectTrigger>{triggerLabel}</SelectTrigger>
				<SelectContent>
					<SelectItem value={NONE_VALUE}>None</SelectItem>
					<SelectItem value={CUSTOM_VALUE}>At a specific time…</SelectItem>
					{renderedOptions.map(opt => (
						<SelectItem key={opt.id} value={opt.id}>
							{opt.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<CustomFollowUpModal
				open={isModalOpen}
				onOpenChange={setModalOpen}
				initial={initialForModal}
				onConfirm={onChange}
				leadTimeZone={zone}
				timeZoneLabel={tzAbbrev}
			/>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	label: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
});
